import { useState, useEffect } from "react";
import { useAtomValue } from "jotai";
import { userInfoAtom } from "@/data/atoms/utilsAtoms";
import Toast from "@/utils/toastManager";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const UserTimeline = ({ userId: propUserId }) => {
  const [userId, setUserId] = useState(propUserId || "");
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState({});
  const userData = useAtomValue(userInfoAtom);

  useEffect(() => {
    if (propUserId) {
      setUserId(propUserId);
      fetchTimeline(propUserId);
    }
  }, [propUserId]);

  const fetchTimeline = async (id) => {
    if (!id) return;
    setLoading(true);
    const response = await fetch(
      `${import.meta.env.VITE_APP_API_URL}/portal/user-timeline/${id}`,
      {
        headers: { Authorization: `Bearer ${userData.token}` },
      },
    );
    const data = await response.json();
    setTimeline(data.data);
    setLoading(false);
  };

  const handleSyncSubscription = async () => {
    if (!timeline?.user?.customerEmail && !timeline?.user?.email) return;

    const email = timeline.user.customerEmail || timeline.user.email;

    const response = await fetch(
      `${import.meta.env.VITE_APP_API_URL}/portal/sync-subscription`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userData.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      },
    );
    const data = await response.json();
    if (data.success) {
      Toast.success("Subscription synced successfully");
      fetchTimeline(userId);
    } else {
      Toast.error("Failed to sync: " + data.message);
    }
  };

  const toggleEventExpand = (eventId) => {
    setExpandedEvents((prev) => ({
      ...prev,
      [eventId]: !prev[eventId],
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTimeline(userId);
  };

  const getStatusClass = (status) => {
    return status?.toLowerCase() || "pending";
  };

  return (
    <div className="timeline-container">
      <form onSubmit={handleSearch} className="timeline-search">
        <div className="search-box">
          <input
            type="text"
            className="search-input"
            placeholder="Enter User ID to view timeline..."
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          />
          <button
            type="submit"
            className="btn btn-primary"
            style={{
              position: "absolute",
              right: "0.5rem",
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            Search
          </button>
        </div>
      </form>

      {loading && <div className="loading">Loading timeline...</div>}

      {timeline && !loading && (
        <>
          <div className="user-profile">
            <div className="profile-header">
              <div className="profile-avatar">
                {(timeline.user.customerEmail || timeline.user.email)
                  ?.charAt(0)
                  .toUpperCase()}
              </div>
              <div className="profile-info">
                <h2>{timeline.user.customerEmail || timeline.user.email}</h2>
                <p>
                  Customer ID:{" "}
                  {timeline.user.CustomerID ||
                    timeline.user.customerId ||
                    timeline.user.userId}
                </p>
              </div>
            </div>

            <div className="profile-identifiers">
              <div className="identifier-item">
                <div className="identifier-label">Chargebee Customer ID</div>
                <div className="identifier-value">
                  {timeline.user.CustomerID ||
                    timeline.user.chargebeeCustomerId ||
                    "N/A"}
                </div>
              </div>
              <div className="identifier-item">
                <div className="identifier-label">Subscription ID</div>
                <div className="identifier-value">
                  {timeline.user.SubscribtionId ||
                    timeline.user.subscriptionId ||
                    "N/A"}
                </div>
              </div>
              <div className="identifier-item">
                <div className="identifier-label">Subscription Status</div>
                <div className="identifier-value">
                  {timeline.user.subscriptionStatus || "N/A"}
                </div>
              </div>
              <div className="identifier-item">
                <div className="identifier-label">Plan</div>
                <div className="identifier-value">
                  {timeline.user.subscription_name || "N/A"}
                </div>
              </div>
            </div>

            <div style={{ marginTop: "1rem", textAlign: "center" }}>
              <button
                className="btn btn-primary"
                onClick={handleSyncSubscription}
                style={{ padding: "0.65rem 1.5rem" }}
              >
                Sync Subscription
              </button>
            </div>
          </div>

          <div className="timeline-events">
            {timeline.events.map((event) => (
              <div
                key={event.id}
                className={`timeline-event ${getStatusClass(event.status)}`}
              >
                <div className="event-header">
                  <div className="event-type">{event.eventType}</div>
                  <span
                    className={`event-status ${getStatusClass(event.status)}`}
                  >
                    {event.status}
                  </span>
                </div>
                <div className="event-time">
                  {new Date(event.timestamp).toLocaleString()}
                </div>
                <div
                  className="event-details"
                  style={{
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {event.description}
                </div>
                {event.metadata && (
                  <div style={{ marginTop: "0.5rem" }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => toggleEventExpand(event.id)}
                      style={{
                        fontSize: "0.75rem",
                        padding: "0.25rem 0.75rem",
                      }}
                    >
                      {expandedEvents[event.id] ? (
                        <>
                          <ArrowDropDownIcon fontSize="small" />
                          Hide Details
                        </>
                      ) : (
                        <>
                          <ArrowRightIcon fontSize="small" />
                          Show Details
                        </>
                      )}
                    </button>
                    {expandedEvents[event.id] && (
                      <div
                        className="event-details"
                        style={{
                          marginTop: "0.5rem",
                          fontSize: "0.84rem",
                          maxHeight: "18.75rem",
                          overflow: "auto",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                          {JSON.stringify(event.metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {!timeline && !loading && userId && propUserId && (
        <div className="empty-state">
          <p>No timeline data found for this user</p>
        </div>
      )}
    </div>
  );
};

export default UserTimeline;
